import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { useGeofencing } from '../contexts/GeofencingContext';
import { Card, CardContent } from '../components/ui/card';
import { MobileSectionMenu } from '../components/shared/MobileSectionMenu';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  AlertTriangle, Download, Edit, Hexagon, Layers, Loader2, Map as MapIcon, MapPin, Plus, Route, Search, Settings2, Shield, Trash2, Upload, X,
} from 'lucide-react';
import { GeofencingSidebar } from '../components/geofencing/GeofencingSidebar';
import { GeofencingPanel } from '../components/geofencing/GeofencingPanel';
import { GeofencingMap } from '../components/geofencing/GeofencingMap';
import { GroupsManagerModal } from '../components/shared/GroupsManagerModal';
import { ConfirmActionDialog } from '../components/shared/ConfirmActionDialog';
import {
  createPlaceBounds,
  createRouteBounds,
  createZoneBounds,
  toGoogleBounds,
} from '../components/geofencing/GeofencingUtils';
import { resolveTimezone, getFileTimestamp } from '../utils/timezone';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const upsertById = (items, item) => {
  const existingIndex = items.findIndex((existingItem) => existingItem.id === item.id);
  if (existingIndex === -1) return [item, ...items];
  const nextItems = [...items];
  nextItems[existingIndex] = item;
  return nextItems;
};

// Static form defaults + export config hoisted to module scope so they are stable
// references (no per-render identity churn) and don't need to be hook dependencies.
const defaultZoneForm = {
  zone_name: '',
  zone_group: '',
  zone_type: 'polygon',
  zone_color: '#2563eb',
  show_name_on_map: true,
  visible_on_map: true,
  geometry: {},
};

const defaultPlaceForm = {
  place_name: '',
  place_group: '',
  show_name_on_map: true,
  visible_on_map: true,
  radius: 100,
  coordinates: [],
  address: '',
  icon: '',
};

const defaultRouteForm = {
  route_name: '',
  route_group: '',
  route_color: '#F59E0B',
  show_name_on_map: true,
  visible_on_map: true,
  path_coordinates: [],
  deviation_distance: 100,
};

const EXPORT_CONFIG = { zones: { endpoint: '/zones', prefix: 'zones' }, places: { endpoint: '/places', prefix: 'places' }, routes: { endpoint: '/geo-routes', prefix: 'routes' } };

export const Geofencing = () => {
  const location = useLocation();
  const { api } = useAuth();
  const {
    isSubpageEnabled, isFeatureEnabled, hasOSMMaps, hasGoogleMaps, hasSatelliteView, hasTerrainView, hasDarkMode,
  } = useFeatureFlags();

  const hasZones = isSubpageEnabled('geofencing_zones');
  const hasPlaces = isSubpageEnabled('geofencing_places');
  const hasRoutes = isSubpageEnabled('geofencing_routes');
  const hasSnapToRoad = isFeatureEnabled('snap_to_road');

  // ── Persisted state from GeofencingContext (survives navigation + F5) ──
  const {
    persistedActiveTab, setPersistedActiveTab,
    mapCenter, setMapCenter,
    mapZoom, setMapZoom,
    mobileGeoView, setMobileGeoView,
    showZonesLayer, setShowZonesLayer,
    showPlacesLayer, setShowPlacesLayer,
    showRoutesLayer, setShowRoutesLayer,
    showPOI, setShowPOI,
    searchQuery, setSearchQuery,
    collapsedGroups, toggleGroupCollapsed,
    selectedZoneId, setSelectedZoneId,
    selectedPlaceId, setSelectedPlaceId,
    selectedRouteId, setSelectedRouteId,
    selectedZoneIds, setSelectedZoneIds,
    selectedPlaceIds, setSelectedPlaceIds,
    selectedRouteIds, setSelectedRouteIds,
  } = useGeofencing();

  const getDefaultTab = useCallback(() => {
    if (hasZones) return 'zones';
    if (hasPlaces) return 'places';
    if (hasRoutes) return 'routes';
    return null;
  }, [hasPlaces, hasRoutes, hasZones]);

  const requestedTab = new URLSearchParams(location.search).get('tab');
  const getRequestedTab = useCallback(() => {
    const allowedTabs = { zones: hasZones, places: hasPlaces, routes: hasRoutes };
    return allowedTabs[requestedTab] ? requestedTab : null;
  }, [hasPlaces, hasRoutes, hasZones, requestedTab]);

  const getDefaultMapType = useCallback(() => {
    const saved = localStorage.getItem('preferred_map_type');
    if (saved) {
      if (saved === 'osm' && hasOSMMaps) return 'osm';
      if (saved !== 'osm' && hasGoogleMaps) return saved;
    }
    if (hasGoogleMaps) return 'roadmap';
    return 'osm';
  }, [hasGoogleMaps, hasOSMMaps]);

  const [activeTab, setActiveTab] = useState(() => {
    const requested = getRequestedTab();
    if (requested) return requested;
    const allowedTabs = { zones: hasZones, places: hasPlaces, routes: hasRoutes };
    if (persistedActiveTab && allowedTabs[persistedActiveTab]) return persistedActiveTab;
    return getDefaultTab();
  });
  const [mapType, setMapTypeState] = useState(getDefaultMapType());
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [infoWindowOpen, setInfoWindowOpen] = useState(null);
  const [addressSearch, setAddressSearch] = useState('');
  const [searchMarker, setSearchMarker] = useState(null);

  // Keep persisted activeTab in sync whenever it changes via UI
  useEffect(() => {
    if (activeTab && activeTab !== persistedActiveTab) setPersistedActiveTab(activeTab);
  }, [activeTab, persistedActiveTab, setPersistedActiveTab]);

  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);

  const [zones, setZones] = useState([]);
  const [places, setPlaces] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [groups, setGroups] = useState([]);

  // Derive selected objects from persisted IDs (re-resolves when data loads)
  const selectedZone = useMemo(() => zones.find((z) => z.id === selectedZoneId) || null, [zones, selectedZoneId]);
  const selectedPlace = useMemo(() => places.find((p) => p.id === selectedPlaceId) || null, [places, selectedPlaceId]);
  const selectedRoute = useMemo(() => routes.find((r) => r.id === selectedRouteId) || null, [routes, selectedRouteId]);

  const [activePanel, setActivePanel] = useState(null);
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [isGroupManageOpen, setIsGroupManageOpen] = useState(false);
  const [pendingGeoAction, setPendingGeoAction] = useState(null);
  const [geoDeleteConfirmText, setGeoDeleteConfirmText] = useState('');

  const [editingZoneId, setEditingZoneId] = useState(null);
  const [editingPlaceId, setEditingPlaceId] = useState(null);
  const [editingRouteId, setEditingRouteId] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);

  const [isDrawingZone, setIsDrawingZone] = useState(false);
  const [isDrawingRoute, setIsDrawingRoute] = useState(false);
  const [isSelectingLocation, setIsSelectingLocation] = useState(false);
  const [isEditingShape, setIsEditingShape] = useState(false);
  const [editInteractionMode, setEditInteractionMode] = useState('reshape');
  const [isSnapping, setIsSnapping] = useState(false);

  const [zoneForm, setZoneForm] = useState({ ...defaultZoneForm });
  const [placeForm, setPlaceForm] = useState({ ...defaultPlaceForm });
  const [routeForm, setRouteForm] = useState({ ...defaultRouteForm });

  const [groupForm, setGroupForm] = useState({ name: '', group_type: 'zone', description: '' });
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupDesc, setEditGroupDesc] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const requested = getRequestedTab();
    if (requested && activeTab !== requested) {
      setActiveTab(requested);
      return;
    }
    const allowedTabs = { zones: hasZones, places: hasPlaces, routes: hasRoutes };
    if (!activeTab || allowedTabs[activeTab] !== true) {
      const nextTab = getDefaultTab();
      if (nextTab) setActiveTab(nextTab);
    }
  }, [activeTab, getDefaultTab, getRequestedTab, hasPlaces, hasRoutes, hasZones]);

  useEffect(() => {
    if (mapType === 'osm' && !hasOSMMaps && hasGoogleMaps) setMapTypeState('roadmap');
    else if (mapType !== 'osm' && !hasGoogleMaps && hasOSMMaps) setMapTypeState('osm');
    else if (mapType !== 'osm' && !hasGoogleMaps && !hasOSMMaps) setMapTypeState('osm');
  }, [hasGoogleMaps, hasOSMMaps, mapType]);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    if (!isEditingShape) setEditInteractionMode('reshape');
  }, [isEditingShape]);

  const safeLeafletOp = useCallback((operation) => {
    try {
      const map = leafletMapRef.current;
      if (map && map.getContainer() && map.getContainer().parentNode) operation(map);
    } catch {
      // Ignore stale map instances during provider swaps.
    }
  }, []);

  const handlePanToLocation = useCallback((lat, lng) => {
    if (mapType === 'osm') {
      safeLeafletOp((map) => map.flyTo([lat, lng], Math.max(map.getZoom(), 15)));
    } else if (mapRef.current) {
      mapRef.current.panTo({ lat, lng });
      if (mapRef.current.getZoom() < 15) mapRef.current.setZoom(15);
    }
  }, [mapType, safeLeafletOp]);

  const focusBounds = useCallback((bounds) => {
    if (!bounds) return;

    if (mapType === 'osm') {
      safeLeafletOp((map) => map.flyToBounds(bounds, { padding: [30, 30], maxZoom: 17 }));
      return;
    }

    if (mapRef.current && window.google?.maps) {
      const googleBounds = toGoogleBounds(bounds);
      if (googleBounds) mapRef.current.fitBounds(googleBounds, 50);
    }
  }, [mapType, safeLeafletOp]);

  const fitToZoneBounds = useCallback((zone) => focusBounds(createZoneBounds(zone)), [focusBounds]);
  const fitToPlaceBounds = useCallback((place) => focusBounds(createPlaceBounds(place)), [focusBounds]);
  const fitToRouteBounds = useCallback((route) => focusBounds(createRouteBounds(route)), [focusBounds]);

  const handleRegionBoundaryLoaded = useCallback((coordinates) => {
    if (!coordinates?.length) return;
    const bounds = createZoneBounds({ geometry: { coordinates } });
    focusBounds(bounds);
  }, [focusBounds]);

  const handleSelectZone = useCallback((zone, options = {}) => {
    setSelectedZoneId(zone?.id || null);
    setSelectedPlaceId(null);
    setSelectedRouteId(null);
    if (options.focus && zone) fitToZoneBounds(zone);
  }, [fitToZoneBounds, setSelectedZoneId, setSelectedPlaceId, setSelectedRouteId]);

  const handleSelectPlace = useCallback((place, options = {}) => {
    setSelectedPlaceId(place?.id || null);
    setSelectedZoneId(null);
    setSelectedRouteId(null);
    if (options.focus && place) fitToPlaceBounds(place);
  }, [fitToPlaceBounds, setSelectedZoneId, setSelectedPlaceId, setSelectedRouteId]);

  const handleSelectRoute = useCallback((route, options = {}) => {
    setSelectedRouteId(route?.id || null);
    setSelectedZoneId(null);
    setSelectedPlaceId(null);
    if (options.focus && route) fitToRouteBounds(route);
  }, [fitToRouteBounds, setSelectedZoneId, setSelectedPlaceId, setSelectedRouteId]);

  const captureCurrentViewport = useCallback(() => {
    if (mapType === 'osm' && leafletMapRef.current?._mapPane && leafletMapRef.current?._loaded) {
      const center = leafletMapRef.current.getCenter();
      const zoom = leafletMapRef.current.getZoom();
      setMapCenter((previous) => (
        previous && Math.abs(previous.lat - center.lat) < 1e-10 && Math.abs(previous.lng - center.lng) < 1e-10
          ? previous
          : { lat: center.lat, lng: center.lng }
      ));
      setMapZoom((previous) => (previous === zoom ? previous : zoom));
      return;
    }

    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      const zoom = mapRef.current.getZoom();
      if (center) {
        const nextCenter = { lat: center.lat(), lng: center.lng() };
        setMapCenter((previous) => (
          previous && Math.abs(previous.lat - nextCenter.lat) < 1e-10 && Math.abs(previous.lng - nextCenter.lng) < 1e-10
            ? previous
            : nextCenter
        ));
      }
      if (typeof zoom === 'number') setMapZoom((previous) => (previous === zoom ? previous : zoom));
    }
  }, [mapType, setMapCenter, setMapZoom]);

  const fetchData = useCallback(async ({ showLoading = false } = {}) => {
    if (showLoading) setLoading(true);
    try {
      const [overlayResponse, groupsResponse] = await Promise.all([
        api.get('/geofences/map-overlay'),
        api.get('/geofence-groups'),
      ]);

      const overlay = overlayResponse.data || {};
      const zonesData = overlay.zones || [];
      const placesData = overlay.places || [];
      const routesData = overlay.routes || [];

      setZones(zonesData);
      setPlaces(placesData);
      setRoutes(routesData);
      setGroups(groupsResponse.data || []);

      return {
        zones: zonesData,
        places: placesData,
        routes: routesData,
        groups: groupsResponse.data || [],
      };
    } catch (error) {
      console.error('Failed to fetch geofencing data:', error);
      toast.error('Failed to load geofencing data');
      return { zones: [], places: [], routes: [], groups: [] };
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData({ showLoading: true });
  }, [fetchData]);

  const resetZoneForm = useCallback(() => {
    setZoneForm({ ...defaultZoneForm });
    setEditingZoneId(null);
    setIsDrawingZone(false);
    setIsEditingShape(false);
  }, []);

  const resetPlaceForm = useCallback(() => {
    setPlaceForm({ ...defaultPlaceForm });
    setEditingPlaceId(null);
    setIsSelectingLocation(false);
  }, []);

  const resetRouteForm = useCallback(() => {
    setRouteForm({ ...defaultRouteForm });
    setEditingRouteId(null);
    setIsDrawingRoute(false);
    setIsEditingShape(false);
  }, []);

  const closePanel = useCallback(() => {
    setActivePanel(null);
    setIsEditingShape(false);
    resetZoneForm();
    resetPlaceForm();
    resetRouteForm();
  }, [resetPlaceForm, resetRouteForm, resetZoneForm]);

  const panelSnapshotRef = useRef(null);
  const [pendingTab, setPendingTab] = useState(null);

  useEffect(() => {
    if (!activePanel) { panelSnapshotRef.current = null; return; }
    const form = activePanel === 'zone' ? zoneForm : activePanel === 'place' ? placeForm : routeForm;
    panelSnapshotRef.current = JSON.stringify(form);
    // Re-snapshot when the panel opens or the edited item changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePanel, editingZoneId, editingPlaceId, editingRouteId]);

  const isPanelDirty = useCallback(() => {
    if (!activePanel || panelSnapshotRef.current == null) return false;
    const form = activePanel === 'zone' ? zoneForm : activePanel === 'place' ? placeForm : routeForm;
    return JSON.stringify(form) !== panelSnapshotRef.current;
  }, [activePanel, zoneForm, placeForm, routeForm]);

  const handleTabChange = useCallback((tab) => {
    if (tab === activeTab && !activePanel) return;
    if (activePanel && isPanelDirty()) {
      setPendingTab(tab);
      return;
    }
    if (activePanel) closePanel();
    setActiveTab(tab);
  }, [activeTab, activePanel, closePanel, isPanelDirty]);

  const toggleGroup = useCallback((name) => {
    toggleGroupCollapsed(name);
  }, [toggleGroupCollapsed]);

  const setMapType = useCallback((type) => {
    if (mapType === 'osm' && leafletMapRef.current?._mapPane && leafletMapRef.current?._loaded) {
      const center = leafletMapRef.current.getCenter();
      const zoom = leafletMapRef.current.getZoom();
      setMapCenter({ lat: center.lat, lng: center.lng });
      setMapZoom(zoom);
    } else if (mapRef.current) {
      const center = mapRef.current.getCenter();
      const zoom = mapRef.current.getZoom();
      if (center) setMapCenter({ lat: center.lat(), lng: center.lng() });
      if (typeof zoom === 'number') setMapZoom(zoom);
    }

    localStorage.setItem('preferred_map_type', type);
    setMapTypeState(type);
  }, [mapType, setMapCenter, setMapZoom]);

  const handleToggleFullscreen = useCallback(() => {
    const mapContainer = document.querySelector('[data-testid="geofencing-map-container"]');
    if (!mapContainer) return;

    if (!document.fullscreenElement) mapContainer.requestFullscreen?.();
    else document.exitFullscreen?.();
  }, []);

  const handleEditZone = useCallback((zone) => {
    handleSelectZone(zone, { focus: true });
    setEditingZoneId(zone.id);
    setZoneForm({
      zone_name: zone.zone_name,
      zone_group: zone.zone_group || '',
      zone_type: zone.zone_type,
      zone_color: zone.zone_color,
      show_name_on_map: zone.show_name_on_map,
      visible_on_map: zone.visible_on_map,
      geometry: zone.geometry,
    });
    setActivePanel('zone');
  }, [handleSelectZone]);

  const handleEditPlace = useCallback((place) => {
    handleSelectPlace(place, { focus: true });
    setEditingPlaceId(place.id);
    setPlaceForm({
      place_name: place.place_name,
      place_group: place.place_group || '',
      show_name_on_map: place.show_name_on_map,
      visible_on_map: place.visible_on_map,
      radius: place.radius,
      coordinates: place.coordinates,
      address: place.address || '',
      icon: place.icon || '',
    });
    setActivePanel('place');
  }, [handleSelectPlace]);

  const handleEditRoute = useCallback((route) => {
    handleSelectRoute(route, { focus: true });
    setEditingRouteId(route.id);
    setRouteForm({
      route_name: route.route_name,
      route_group: route.route_group || '',
      route_color: route.route_color,
      show_name_on_map: route.show_name_on_map,
      visible_on_map: route.visible_on_map,
      path_coordinates: route.path_coordinates,
      deviation_distance: route.deviation_distance,
    });
    setActivePanel('route');
  }, [handleSelectRoute]);

  const handleSaveZone = useCallback(async () => {
    if (!zoneForm.zone_name) {
      toast.error('Please provide a zone name');
      return;
    }

    const hasGeometry = zoneForm.geometry?.coordinates?.length >= 3 || (zoneForm.geometry?.type === 'circle' && zoneForm.geometry?.center);
    if (!hasGeometry) {
      toast.error('Please draw the zone on the map');
      return;
    }

    setSaving(true);
    try {
      let savedZone;
      if (editingZoneId) {
        const response = await api.put(`/zones/${editingZoneId}`, zoneForm);
        savedZone = response.data;
        setZones((previous) => upsertById(previous, response.data));
        toast.success('Zone updated');
      } else {
        const response = await api.post('/zones', zoneForm);
        savedZone = response.data;
        setZones((previous) => upsertById(previous, response.data));
        toast.success('Zone created');
      }

      captureCurrentViewport();
      closePanel();
      if (savedZone) handleSelectZone(savedZone, { focus: false });
    } catch {
      toast.error(editingZoneId ? 'Failed to update zone' : 'Failed to create zone');
    } finally {
      setSaving(false);
    }
  }, [api, captureCurrentViewport, closePanel, editingZoneId, handleSelectZone, zoneForm]);

  const handleSavePlace = useCallback(async () => {
    if (!placeForm.place_name) {
      toast.error('Please provide a place name');
      return;
    }
    if (!placeForm.icon) {
      toast.error('Please select an icon for the place');
      return;
    }
    if (!placeForm.coordinates || placeForm.coordinates.length < 2) {
      toast.error('Please select a location on the map');
      return;
    }

    setSaving(true);
    try {
      let savedPlace;
      if (editingPlaceId) {
        const response = await api.put(`/places/${editingPlaceId}`, placeForm);
        savedPlace = response.data;
        setPlaces((previous) => upsertById(previous, response.data));
        toast.success('Place updated');
      } else {
        const response = await api.post('/places', placeForm);
        savedPlace = response.data;
        setPlaces((previous) => upsertById(previous, response.data));
        toast.success('Place created');
      }

      captureCurrentViewport();
      closePanel();
      if (savedPlace) handleSelectPlace(savedPlace, { focus: false });
    } catch {
      toast.error(editingPlaceId ? 'Failed to update place' : 'Failed to create place');
    } finally {
      setSaving(false);
    }
  }, [api, captureCurrentViewport, closePanel, editingPlaceId, handleSelectPlace, placeForm]);

  const handleSaveRoute = useCallback(async () => {
    if (!routeForm.route_name) {
      toast.error('Please provide a route name');
      return;
    }
    if (!routeForm.path_coordinates || routeForm.path_coordinates.length < 2) {
      toast.error('Please draw at least 2 waypoints');
      return;
    }

    setSaving(true);
    try {
      let savedRoute;
      if (editingRouteId) {
        const response = await api.put(`/geo-routes/${editingRouteId}`, routeForm);
        savedRoute = response.data;
        setRoutes((previous) => upsertById(previous, response.data));
        toast.success('Route updated');
      } else {
        const response = await api.post('/geo-routes', routeForm);
        savedRoute = response.data;
        setRoutes((previous) => upsertById(previous, response.data));
        toast.success('Route created');
      }

      captureCurrentViewport();
      closePanel();
      if (savedRoute) handleSelectRoute(savedRoute, { focus: false });
    } catch {
      toast.error(editingRouteId ? 'Failed to update route' : 'Failed to create route');
    } finally {
      setSaving(false);
    }
  }, [api, captureCurrentViewport, closePanel, editingRouteId, handleSelectRoute, routeForm]);

  const handleDeleteZone = useCallback((zoneId) => {
    const zone = zones.find((item) => item.id === zoneId);
    setPendingGeoAction({
      title: 'Delete Zone?',
      requireTyped: true,
      message: `Are you sure you want to delete "${zone?.zone_name || 'this zone'}"? This action cannot be undone.`,
      action: async () => {
        try {
          await api.delete(`/zones/${zoneId}`);
          toast.success('Zone deleted');
          if (selectedZone?.id === zoneId) setSelectedZoneId(null);
          setZones((previous) => previous.filter((item) => item.id !== zoneId));
        } catch {
          toast.error('Failed to delete zone');
        }
      },
    });
  }, [api, selectedZone?.id, zones, setSelectedZoneId]);

  const handleDeletePlace = useCallback((placeId) => {
    const place = places.find((item) => item.id === placeId);
    setPendingGeoAction({
      title: 'Delete Place?',
      requireTyped: true,
      message: `Are you sure you want to delete "${place?.place_name || 'this place'}"? This action cannot be undone.`,
      action: async () => {
        try {
          await api.delete(`/places/${placeId}`);
          toast.success('Place deleted');
          if (selectedPlace?.id === placeId) setSelectedPlaceId(null);
          setPlaces((previous) => previous.filter((item) => item.id !== placeId));
        } catch {
          toast.error('Failed to delete place');
        }
      },
    });
  }, [api, places, selectedPlace?.id, setSelectedPlaceId]);

  const handleDeleteRoute = useCallback((routeId) => {
    const route = routes.find((item) => item.id === routeId);
    setPendingGeoAction({
      title: 'Delete Route?',
      requireTyped: true,
      message: `Are you sure you want to delete "${route?.route_name || 'this route'}"? This action cannot be undone.`,
      action: async () => {
        try {
          await api.delete(`/geo-routes/${routeId}`);
          toast.success('Route deleted');
          if (selectedRoute?.id === routeId) setSelectedRouteId(null);
          setRoutes((previous) => previous.filter((item) => item.id !== routeId));
        } catch {
          toast.error('Failed to delete route');
        }
      },
    });
  }, [api, routes, selectedRoute?.id, setSelectedRouteId]);

  const handleDuplicateZone = useCallback(async (zone) => {
    try {
      captureCurrentViewport();
      const response = await api.post('/zones', {
        zone_name: `${zone.zone_name} (Copy)`,
        zone_group: zone.zone_group,
        zone_type: zone.zone_type,
        zone_color: zone.zone_color,
        show_name_on_map: zone.show_name_on_map,
        visible_on_map: zone.visible_on_map,
        geometry: zone.geometry,
      });
      setZones((previous) => upsertById(previous, response.data));
      toast.success('Zone duplicated');
    } catch {
      toast.error('Failed to duplicate zone');
    }
  }, [api, captureCurrentViewport]);

  const handleDuplicatePlace = useCallback(async (place) => {
    try {
      captureCurrentViewport();
      const response = await api.post('/places', {
        place_name: `${place.place_name} (Copy)`,
        place_group: place.place_group,
        show_name_on_map: place.show_name_on_map,
        visible_on_map: place.visible_on_map,
        radius: place.radius,
        coordinates: place.coordinates,
        address: place.address,
        icon: place.icon,
      });
      setPlaces((previous) => upsertById(previous, response.data));
      toast.success('Place duplicated');
    } catch {
      toast.error('Failed to duplicate place');
    }
  }, [api, captureCurrentViewport]);

  const handleDuplicateRoute = useCallback(async (route) => {
    try {
      captureCurrentViewport();
      const response = await api.post('/geo-routes', {
        route_name: `${route.route_name} (Copy)`,
        route_group: route.route_group,
        route_color: route.route_color,
        show_name_on_map: route.show_name_on_map,
        visible_on_map: route.visible_on_map,
        path_coordinates: route.path_coordinates,
        deviation_distance: route.deviation_distance,
      });
      setRoutes((previous) => upsertById(previous, response.data));
      toast.success('Route duplicated');
    } catch {
      toast.error('Failed to duplicate route');
    }
  }, [api, captureCurrentViewport]);

  const handleSaveGroup = useCallback(async () => {
    if (!groupForm.name) {
      toast.error('Please provide a group name');
      return;
    }

    setSaving(true);
    try {
      await api.post('/geofence-groups', groupForm);
      toast.success('Group created');
      setIsGroupDialogOpen(false);
      setGroupForm({
        name: '',
        group_type: activeTab === 'zones' ? 'zone' : activeTab === 'places' ? 'place' : 'route',
        description: '',
      });
      await fetchData();
    } catch {
      toast.error('Failed to create group');
    } finally {
      setSaving(false);
    }
  }, [activeTab, api, fetchData, groupForm]);

  const handleDeleteGroup = useCallback(async (groupId) => {
    try {
      await api.delete(`/geofence-groups/${groupId}`);
      toast.success('Group deleted');
      await fetchData();
    } catch {
      toast.error('Failed to delete group');
    }
  }, [api, fetchData]);

  const handleUpdateGroup = useCallback(async (groupId) => {
    if (!editGroupName.trim()) return;

    try {
      await api.put(`/geofence-groups/${groupId}`, { name: editGroupName, description: editGroupDesc });
      toast.success('Group updated');
      setEditingGroupId(null);
      await fetchData();
    } catch {
      toast.error('Failed to update group');
    }
  }, [api, editGroupDesc, editGroupName, fetchData]);

  const handleOpenCreateGroup = useCallback(() => {
    setGroupForm({
      name: '',
      group_type: activeTab === 'zones' ? 'zone' : activeTab === 'places' ? 'place' : 'route',
      description: '',
    });
    setIsGroupDialogOpen(true);
  }, [activeTab]);

  const handleOpenAddZone = useCallback(() => {
    resetZoneForm();
    setActivePanel('zone');
  }, [resetZoneForm]);

  const handleOpenAddPlace = useCallback(() => {
    resetPlaceForm();
    setActivePanel('place');
  }, [resetPlaceForm]);

  const handleOpenAddRoute = useCallback(() => {
    resetRouteForm();
    setActivePanel('route');
  }, [resetRouteForm]);

  const activeSelectedIds = activeTab === 'zones' ? selectedZoneIds : activeTab === 'places' ? selectedPlaceIds : selectedRouteIds;

  const handleExport = useCallback(async (format) => {
    const cfg = EXPORT_CONFIG[activeTab];
    if (!cfg) return;
    try {
      let url = `${cfg.endpoint}/export?format=${format}`;
      if (activeSelectedIds.length > 0) {
        url += `&ids=${activeSelectedIds.join(',')}`;
      }
      const response = await api.get(url, { responseType: 'blob' });
      const ext = format === 'geojson' ? 'geojson' : 'csv';
      const blob = new Blob([response.data], { type: format === 'geojson' ? 'application/geo+json' : 'text/csv' });
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      const ts = getFileTimestamp(resolveTimezone());
      a.download = `${cfg.prefix}_export_${ts}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      const count = activeSelectedIds.length > 0 ? activeSelectedIds.length : '';
      toast.success(`Exported ${count ? count + ' ' : ''}${activeTab} as ${ext.toUpperCase()}`);
      if (activeTab === 'zones') setSelectedZoneIds([]);
      else if (activeTab === 'places') setSelectedPlaceIds([]);
      else setSelectedRouteIds([]);
    } catch {
      toast.error(`Failed to export ${activeTab}`);
    }
  }, [activeTab, activeSelectedIds, api, setSelectedZoneIds, setSelectedPlaceIds, setSelectedRouteIds]);

  const handleDeleteSelected = useCallback(() => {
    if (activeSelectedIds.length === 0) return;
    const cfg = EXPORT_CONFIG[activeTab];
    if (!cfg) return;
    const label = activeTab === 'zones' ? 'zone' : activeTab === 'places' ? 'place' : 'route';
    setPendingGeoAction({
      title: `Delete ${activeSelectedIds.length} ${label}${activeSelectedIds.length === 1 ? '' : 's'}?`,
      requireTyped: true,
      message: `Are you sure you want to delete ${activeSelectedIds.length} selected ${label}(s)? They will also be removed from any Event Rules that reference them. This action cannot be undone.`,
      action: async () => {
        try {
          await Promise.all(activeSelectedIds.map((id) => api.delete(`${cfg.endpoint}/${id}`)));
          toast.success(`Deleted ${activeSelectedIds.length} ${label}(s)`);
          if (activeTab === 'zones') setSelectedZoneIds([]);
          else if (activeTab === 'places') setSelectedPlaceIds([]);
          else setSelectedRouteIds([]);
          fetchData();
        } catch {
          toast.error(`Failed to delete some ${label}(s)`);
          fetchData();
        }
      },
    });
  }, [activeTab, activeSelectedIds, api, fetchData, setSelectedZoneIds, setSelectedPlaceIds, setSelectedRouteIds]);

  const handleImport = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const cfg = EXPORT_CONFIG[activeTab];
    if (!cfg) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`${cfg.endpoint}/import`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const result = response.data;
      if (result.imported > 0) {
        toast.success(`Imported ${result.imported} ${activeTab}`);
        fetchData();
      }
      if (result.errors?.length > 0) {
        toast.error(`${result.errors.length} error(s) during import`);
        console.warn('Import errors:', result.errors);
      }
      if (result.imported === 0 && (!result.errors || result.errors.length === 0)) {
        toast.info('No items found to import');
      }
    } catch (err) {
      toast.error(`Import failed: ${err.response?.data?.detail || 'Unknown error'}`);
    }
  }, [activeTab, api, fetchData]);

  const handleToggleZoneDrawing = useCallback(() => {
    setIsEditingShape(false);
    setIsDrawingZone((previous) => {
      const next = !previous;
      if (!previous) setZoneForm((current) => ({ ...current, geometry: {} }));
      return next;
    });
  }, []);

  const handleToggleRouteDrawing = useCallback(() => {
    setIsEditingShape(false);
    setIsDrawingRoute((previous) => {
      const next = !previous;
      if (!previous) setRouteForm((current) => ({ ...current, path_coordinates: [] }));
      return next;
    });
  }, []);

  const handleSnapToRoad = useCallback(async () => {
    if (routeForm.path_coordinates.length < 2) {
      toast.error('Draw at least 2 waypoints before snapping');
      return;
    }
    setIsSnapping(true);
    try {
      const points = routeForm.path_coordinates.map((c) => ({ lat: c[0], lng: c[1] }));
      const response = await api.post('/snap-to-road', { points });
      const result = response.data;
      if (result.error) {
        toast.error(`Snap to road failed: ${result.error}`);
        return;
      }
      if (result.snappedPoints?.length >= 2) {
        const snappedCoords = result.snappedPoints.map((p) => [p.lat, p.lng]);
        setRouteForm((prev) => ({ ...prev, path_coordinates: snappedCoords }));
        toast.success(`Route snapped to road (${result.snappedPoints.length} points)`);
      } else {
        toast.error('Could not snap route to road — no matching roads found');
      }
    } catch {
      toast.error('Failed to snap route to road');
    } finally {
      setIsSnapping(false);
    }
  }, [api, routeForm.path_coordinates]);

  const handleToggleSelectItem = useCallback((id) => {
    const setter = activeTab === 'zones' ? setSelectedZoneIds : activeTab === 'places' ? setSelectedPlaceIds : setSelectedRouteIds;
    setter((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  }, [activeTab, setSelectedZoneIds, setSelectedPlaceIds, setSelectedRouteIds]);

  const handleToggleSelectAll = useCallback((checked) => {
    const items = activeTab === 'zones' ? zones : activeTab === 'places' ? places : routes;
    const setter = activeTab === 'zones' ? setSelectedZoneIds : activeTab === 'places' ? setSelectedPlaceIds : setSelectedRouteIds;
    setter(checked ? items.map((i) => i.id) : []);
  }, [activeTab, zones, places, routes, setSelectedZoneIds, setSelectedPlaceIds, setSelectedRouteIds]);

  const currentGroups = useMemo(() => groups.filter((group) => (
    activeTab === 'zones' ? group.group_type === 'zone'
      : activeTab === 'places' ? group.group_type === 'place'
        : group.group_type === 'route'
  )), [activeTab, groups]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]" data-testid="geofencing-loading-state">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="geofencing-page">
      <div className="hidden lg:flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#2563eb]/10" data-testid="geofencing-page-icon-wrap">
          <MapIcon className="w-6 h-6 text-[#2563eb]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground" data-testid="geofencing-page-title">Geofencing</h1>
          <p className="text-sm text-muted-foreground" data-testid="geofencing-page-subtitle">Define virtual boundaries, locations, and routes</p>
        </div>
      </div>

      <MobileSectionMenu
        items={[
          hasZones && { id: 'zones', label: 'Zones', icon: Hexagon },
          hasPlaces && { id: 'places', label: 'Places', icon: MapPin },
          hasRoutes && { id: 'routes', label: 'Routes', icon: Route },
        ].filter(Boolean)}
        activeId={activeTab}
        onChange={handleTabChange}
        testIdPrefix="geofencing-tabs"
      />
      <div className="hidden sm:flex gap-2 flex-wrap" data-testid="geofencing-tab-bar">
        {hasZones ? (
          <Button onClick={() => handleTabChange('zones')} data-testid="tab-zones" className={`gap-2 min-w-[170px] justify-center ${activeTab === 'zones' ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
            <Hexagon className="w-4 h-4" /> Zones
          </Button>
        ) : null}
        {hasPlaces ? (
          <Button onClick={() => handleTabChange('places')} data-testid="tab-places" className={`gap-2 min-w-[170px] justify-center ${activeTab === 'places' ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
            <MapPin className="w-4 h-4" /> Places
          </Button>
        ) : null}
        {hasRoutes ? (
          <Button onClick={() => handleTabChange('routes')} data-testid="tab-routes" className={`gap-2 min-w-[170px] justify-center ${activeTab === 'routes' ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
            <Route className="w-4 h-4" /> Routes
          </Button>
        ) : null}
      </div>

      {activeTab ? (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {activeTab === 'zones' ? <Hexagon className="w-5 h-5 text-foreground" /> : activeTab === 'places' ? <MapPin className="w-5 h-5 text-foreground" /> : <Route className="w-5 h-5 text-foreground" />}
                <h2 className="text-xl font-semibold" data-testid={`${activeTab}-subpage-title`}>
                  {activeTab === 'zones' ? 'Zones' : activeTab === 'places' ? 'Places' : 'Routes'}
                </h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1" data-testid={`${activeTab}-subpage-description`}>
                {activeTab === 'zones' ? 'Define polygonal or circular boundaries and receive alerts for enter, exit, and dwell events.' : activeTab === 'places' ? 'Pin recurring locations with a radius to track arrivals, departures, and time on site.' : 'Draw preferred paths and get notified when vehicles deviate beyond the configured tolerance.'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="geofencing-toolbar">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={`Search ${activeTab}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-10 pr-9"
                data-testid="geofence-search"
              />
              {searchQuery && (
                <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSearchQuery('')} data-testid="geofence-clear-search-button"><X className="w-4 h-4" /></Button>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setIsGroupManageOpen(true)} data-testid="manage-groups-btn">
              <Settings2 className="w-4 h-4 mr-2" /> Manage Groups
            </Button>
            <input type="file" accept=".csv,.geojson,.json" onChange={handleImport} className="hidden" id="geofence-import-input" data-testid="geofence-import-input" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="import-btn">
                  <Download className="w-4 h-4 mr-2" /> Import
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => { const input = document.getElementById('geofence-import-input'); input.accept = '.csv'; input.click(); }} data-testid="import-csv-option">
                  Import CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { const input = document.getElementById('geofence-import-input'); input.accept = '.geojson,.json'; input.click(); }} data-testid="import-geojson-option">
                  Import GeoJSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { const input = document.getElementById('geofence-import-input'); input.accept = '.plc'; input.click(); }} data-testid="import-plc-option">
                  Import PLC (old system)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={activeSelectedIds.length === 0} data-testid="export-btn">
                  <Upload className="w-4 h-4 mr-2" /> {activeSelectedIds.length > 0 ? `Export (${activeSelectedIds.length})` : 'Export'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv-option">
                  Export CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('geojson')} data-testid="export-geojson-option">
                  Export GeoJSON
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {activeSelectedIds.length > 0 && (
              <Button variant="destructive" size="sm" onClick={handleDeleteSelected} data-testid="geofence-delete-selected-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Delete ({activeSelectedIds.length})
              </Button>
            )}
            {activeTab === 'zones' ? (
              <Button size="sm" onClick={handleOpenAddZone} className="bg-[#2563eb] hover:bg-[#1d4ed8] min-w-[170px] justify-center" data-testid="add-zone-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Zone
              </Button>
            ) : null}
            {activeTab === 'places' ? (
              <Button size="sm" onClick={handleOpenAddPlace} className="bg-[#2563eb] hover:bg-[#1d4ed8] min-w-[170px] justify-center" data-testid="add-place-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Place
              </Button>
            ) : null}
            {activeTab === 'routes' ? (
              <Button size="sm" onClick={handleOpenAddRoute} className="bg-[#2563eb] hover:bg-[#1d4ed8] min-w-[170px] justify-center" data-testid="add-route-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Route
              </Button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-start justify-between gap-2" data-testid="geofencing-counts-row">
            <Badge variant="outline" data-testid={`${activeTab}-total-badge`}>
              {(activeTab === 'zones' ? zones.length : activeTab === 'places' ? places.length : routes.length)} Total
            </Badge>
            <div className="flex items-center gap-4 rounded-md border bg-card px-3 -mt-1 py-0.5" data-testid="geofence-layer-toggles">
              <span className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wider">Map Layers</span>
              <div className="flex items-center gap-1.5">
                <Hexagon className="w-3.5 h-3.5 text-[#2563eb]" />
                <span className="text-xs" data-testid="zones-layer-label">Zones</span>
                <Switch checked={showZonesLayer} onCheckedChange={setShowZonesLayer} data-testid="toggle-zones-layer" />
              </div>
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#10B981]" />
                <span className="text-xs" data-testid="places-layer-label">Places</span>
                <Switch checked={showPlacesLayer} onCheckedChange={setShowPlacesLayer} data-testid="toggle-places-layer" />
              </div>
              <div className="flex items-center gap-1.5">
                <Route className="w-3.5 h-3.5 text-[#F59E0B]" />
                <span className="text-xs" data-testid="routes-layer-label">Routes</span>
                <Switch checked={showRoutesLayer} onCheckedChange={setShowRoutesLayer} data-testid="toggle-routes-layer" />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {activeTab ? (
        <div className="grid grid-cols-2 gap-2 lg:hidden" data-testid="geofencing-mobile-view-toggle">
          <Button
            type="button"
            variant={mobileGeoView === 'list' ? 'default' : 'outline'}
            className={`h-10 gap-2 ${mobileGeoView === 'list' ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white' : 'bg-white dark:bg-slate-800'}`}
            onClick={() => setMobileGeoView('list')}
            aria-pressed={mobileGeoView === 'list'}
            data-testid="geofencing-mobile-list-toggle"
          >
            <Layers className="w-4 h-4" /> List
          </Button>
          <Button
            type="button"
            variant={mobileGeoView === 'map' ? 'default' : 'outline'}
            className={`h-10 gap-2 ${mobileGeoView === 'map' ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white' : 'bg-white dark:bg-slate-800'}`}
            onClick={() => setMobileGeoView('map')}
            aria-pressed={mobileGeoView === 'map'}
            data-testid="geofencing-mobile-map-toggle"
          >
            <MapIcon className="w-4 h-4" /> Map
          </Button>
        </div>
      ) : null}

      {!activeTab ? (
        <Card data-testid="geofencing-no-access-card">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Shield className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
            <p className="text-lg font-medium text-muted-foreground" data-testid="geofencing-no-access-title">No Access</p>
            <p className="text-sm text-muted-foreground mt-2" data-testid="geofencing-no-access-copy">You don&apos;t have permission to access any sections of Geofencing.</p>
          </CardContent>
        </Card>
      ) : null}

      {activeTab ? (
        <div className="flex flex-col gap-4 lg:flex-row lg:h-[calc(100vh-22rem)] lg:min-h-[30rem] xl:h-[calc(100vh-20.5rem)]" data-testid="geofencing-mobile-responsive-workspace">
          <div className={`${mobileGeoView === 'list' ? 'flex' : 'hidden'} h-[calc(100vh-19rem)] min-h-[30rem] lg:contents`} data-testid="geofencing-mobile-list-pane">
          <GeofencingSidebar
            activeTab={activeTab}
            inlineEditor={activePanel ? (
              <GeofencingPanel
                activePanel={activePanel}
                groups={groups}
                editingZoneId={editingZoneId}
                editingPlaceId={editingPlaceId}
                editingRouteId={editingRouteId}
                zoneForm={zoneForm}
                setZoneForm={setZoneForm}
                placeForm={placeForm}
                setPlaceForm={setPlaceForm}
                routeForm={routeForm}
                setRouteForm={setRouteForm}
                isDrawingZone={isDrawingZone}
                onToggleDrawingZone={handleToggleZoneDrawing}
                isDrawingRoute={isDrawingRoute}
                onToggleDrawingRoute={handleToggleRouteDrawing}
                isSelectingLocation={isSelectingLocation}
                onToggleSelectingLocation={() => {
                  setIsEditingShape(false);
                  setIsSelectingLocation((previous) => !previous);
                }}
                isEditingShape={isEditingShape}
                onToggleEditingShape={() => setIsEditingShape((previous) => !previous)}
                onPanToLocation={handlePanToLocation}
                saving={saving}
                onClose={closePanel}
                onSave={activePanel === 'zone' ? handleSaveZone : activePanel === 'place' ? handleSavePlace : handleSaveRoute}
                hasSnapToRoad={hasSnapToRoad}
                isSnapping={isSnapping}
                onSnapToRoad={handleSnapToRoad}
                api={api}
                onRegionBoundaryLoaded={handleRegionBoundaryLoaded}
              />
            ) : null}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            zones={zones}
            places={places}
            routes={routes}
            groups={groups}
            selectedZone={selectedZone}
            selectedPlace={selectedPlace}
            selectedRoute={selectedRoute}
            collapsedGroups={collapsedGroups}
            onToggleGroup={toggleGroup}
            onOpenAddZone={handleOpenAddZone}
            onOpenAddPlace={handleOpenAddPlace}
            onOpenAddRoute={handleOpenAddRoute}
            onSelectZone={(zone) => handleSelectZone(zone, { focus: true })}
            onSelectPlace={(place) => handleSelectPlace(place, { focus: true })}
            onSelectRoute={(route) => handleSelectRoute(route, { focus: true })}
            onEditZone={handleEditZone}
            onEditPlace={handleEditPlace}
            onEditRoute={handleEditRoute}
            onDeleteZone={handleDeleteZone}
            onDeletePlace={handleDeletePlace}
            onDeleteRoute={handleDeleteRoute}
            onDuplicateZone={handleDuplicateZone}
            onDuplicatePlace={handleDuplicatePlace}
            onDuplicateRoute={handleDuplicateRoute}
            showZonesLayer={showZonesLayer}
            onShowZonesLayerChange={setShowZonesLayer}
            showPlacesLayer={showPlacesLayer}
            onShowPlacesLayerChange={setShowPlacesLayer}
            showRoutesLayer={showRoutesLayer}
            onShowRoutesLayerChange={setShowRoutesLayer}
            selectedZoneIds={selectedZoneIds}
            selectedPlaceIds={selectedPlaceIds}
            selectedRouteIds={selectedRouteIds}
            onToggleSelectItem={handleToggleSelectItem}
            onToggleSelectAll={handleToggleSelectAll}
          />
          </div>

          <div className={`${mobileGeoView === 'map' ? 'block' : 'hidden'} h-[calc(100vh-19rem)] min-h-[30rem] lg:contents`} data-testid="geofencing-mobile-map-pane">
          <GeofencingMap
            mapType={mapType}
            onMapTypeChange={setMapType}
            hasOSMMaps={hasOSMMaps}
            hasGoogleMaps={hasGoogleMaps}
            hasSatelliteView={hasSatelliteView}
            hasTerrainView={hasTerrainView}
            hasDarkMode={hasDarkMode}
            mapCenter={mapCenter}
            mapZoom={mapZoom}
            mapRef={mapRef}
            leafletMapRef={leafletMapRef}
            showPOI={showPOI}
            onTogglePOI={() => setShowPOI((previous) => !previous)}
            isFullscreen={isFullscreen}
            onToggleFullscreen={handleToggleFullscreen}
            activePanel={activePanel}
            editingZoneId={editingZoneId}
            editingPlaceId={editingPlaceId}
            editingRouteId={editingRouteId}
            isDrawingZone={isDrawingZone}
            onDrawingZoneChange={setIsDrawingZone}
            isDrawingRoute={isDrawingRoute}
            onDrawingRouteChange={setIsDrawingRoute}
            isSelectingLocation={isSelectingLocation}
            onSelectingLocationChange={setIsSelectingLocation}
            isEditingShape={isEditingShape}
            editInteractionMode={editInteractionMode}
            onEditInteractionModeChange={setEditInteractionMode}
            zoneForm={zoneForm}
            onZoneGeometryChange={(geometry) => setZoneForm((previous) => ({ ...previous, geometry }))}
            placeForm={placeForm}
            onPlaceChange={setPlaceForm}
            routeForm={routeForm}
            onRoutePathChange={(pathCoordinates) => setRouteForm((previous) => ({ ...previous, path_coordinates: pathCoordinates }))}
            zones={zones}
            places={places}
            routes={routes}
            selectedZone={selectedZone}
            selectedPlace={selectedPlace}
            selectedRoute={selectedRoute}
            showZonesLayer={showZonesLayer}
            showPlacesLayer={showPlacesLayer}
            showRoutesLayer={showRoutesLayer}
            infoWindowOpen={infoWindowOpen}
            onInfoWindowOpenChange={setInfoWindowOpen}
            addressSearch={addressSearch}
            onAddressSearchChange={setAddressSearch}
            searchMarker={searchMarker}
            onSearchMarkerChange={setSearchMarker}
            onSelectZone={handleSelectZone}
            onSelectPlace={handleSelectPlace}
            onSelectRoute={handleSelectRoute}
            onViewportChange={({ center, zoom }) => {
              if (center) {
                setMapCenter((previous) => (
                  previous && Math.abs(previous.lat - center.lat) < 1e-10 && Math.abs(previous.lng - center.lng) < 1e-10
                    ? previous
                    : center
                ));
              }
              if (typeof zoom === 'number') setMapZoom((previous) => (previous === zoom ? previous : zoom));
            }}
          />
          </div>
        </div>
      ) : null}

      <GroupsManagerModal
        open={isGroupManageOpen}
        onOpenChange={setIsGroupManageOpen}
        api={api}
        apiBase="/geofence-groups"
        apiParams={{ group_type: activeTab === 'zones' ? 'zone' : activeTab === 'places' ? 'place' : 'route' }}
        createPayloadExtra={{ group_type: activeTab === 'zones' ? 'zone' : activeTab === 'places' ? 'place' : 'route' }}
        items={activeTab === 'zones' ? zones : activeTab === 'places' ? places : routes}
        itemLabel={(it) => it.zone_name || it.place_name || it.route_name || it.name || ''}
        itemSubLabel={(it) => it.description || ''}
        itemIdField="id"
        itemGroupField={activeTab === 'zones' ? 'zone_group' : activeTab === 'places' ? 'place_group' : 'route_group'}
        membershipMode="item_group_id"
        allocateEndpoints={{ add: 'add-items', remove: 'remove-items' }}
        title={`Manage ${activeTab === 'zones' ? 'Zone' : activeTab === 'places' ? 'Place' : 'Route'} Groups`}
        itemLabelPlural={activeTab}
        onSaved={fetchData}
        testIdPrefix="geofence-groups"
      />

      <Dialog open={!!pendingGeoAction} onOpenChange={(open) => { if (!open) { setPendingGeoAction(null); setGeoDeleteConfirmText(''); } }}>
        <DialogContent className="max-w-sm" data-testid="delete-confirmation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" />{pendingGeoAction?.title}</DialogTitle>
            <DialogDescription>{pendingGeoAction?.message}</DialogDescription>
          </DialogHeader>
          {pendingGeoAction?.requireTyped && (
            <div className="space-y-3">
              <p className="text-sm">To confirm, type the following exactly:</p>
              <code className="block p-2 bg-muted rounded text-sm font-mono font-bold">DELETE</code>
              <Input
                value={geoDeleteConfirmText}
                onChange={(e) => setGeoDeleteConfirmText(e.target.value)}
                placeholder="Type confirmation text..."
                className="font-mono"
                autoFocus
                data-testid="geo-delete-confirm-input"
              />
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => { setPendingGeoAction(null); setGeoDeleteConfirmText(''); }} data-testid="cancel-delete-btn">Cancel</Button>
            <Button
              size="sm"
              variant="destructive"
              data-testid="confirm-delete-btn"
              disabled={pendingGeoAction?.requireTyped && geoDeleteConfirmText !== 'DELETE'}
              onClick={async () => { await pendingGeoAction?.action(); setPendingGeoAction(null); setGeoDeleteConfirmText(''); }}
            >Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmActionDialog
        open={!!pendingTab}
        onOpenChange={(o) => { if (!o) setPendingTab(null); }}
        title="Discard unsaved changes?"
        message={`You have unsaved changes in the open ${activePanel || ''} panel. Switching tabs will discard them.`}
        confirmLabel="Discard changes"
        onConfirm={() => {
          closePanel();
          setActiveTab(pendingTab);
          setPendingTab(null);
        }}
        testPrefix="unsaved-changes"
      />
    </div>
  );
};